const GOLD = "hsl(43 60% 55%)";
const GOLD_DIM = "hsl(43 40% 38%)";
const INK = "hsl(45 20% 96%)";
const MUTED = "hsl(45 10% 60%)";
const TRACK = "hsl(218 25% 16%)";

const num = (s) => parseFloat(String(s).replace(/[€,%\s]/g, "").replace(",", "")) || 0;
const fontA = { fontFamily: "var(--font-accent, inherit)", letterSpacing: "0.08em" };

const SvgLabel = ({ text, ...p }) => (
  <text {...p} style={fontA}>{text}</text>
);

export const PriceBarChart = ({ rows }) => {
  const cap = 24000;
  const rowH = 40;
  const barX = 200;
  const barMax = 330;
  const h = rows.length * rowH + 24;
  return (
    <svg
      viewBox={`0 0 600 ${h}`}
      role="img"
      aria-label="Median price per square metre by area, Q1 2026"
      data-testid="report-price-chart"
      className="w-full h-auto"
    >
      {rows.map((r, i) => {
        const v = num(r.median);
        const clipped = v > cap;
        const w = Math.max(6, (Math.min(v, cap) / cap) * barMax);
        const y = i * rowH + 14;
        return (
          <g key={r.area}>
            <text x="0" y={y + 12} fill={INK} fontSize="11.5" style={fontA}>
              {r.area.length > 26 ? r.area.slice(0, 25) + "…" : r.area}
            </text>
            <rect x={barX} y={y} width={barMax} height={16} fill={TRACK} />
            <rect x={barX} y={y} width={w} height={16} fill={i === rows.length - 1 && clipped ? GOLD_DIM : GOLD}>
              <animate attributeName="width" from="0" to={w} dur="0.7s" fill="freeze" />
            </rect>
            {clipped && (
              <g stroke="hsl(218 40% 6%)" strokeWidth="3">
                <line x1={barX + barMax - 26} y1={y - 2} x2={barX + barMax - 34} y2={y + 18} />
                <line x1={barX + barMax - 18} y1={y - 2} x2={barX + barMax - 26} y2={y + 18} />
              </g>
            )}
            <text x={barX + Math.min(w, barMax) + 8} y={y + 12} fill={GOLD} fontSize="11.5" style={fontA}>
              {r.median}{clipped ? " ↗" : ""}
            </text>
          </g>
        );
      })}
      <text x="0" y={h - 2} fill={MUTED} fontSize="10" style={fontA}>
        Bar scale capped at €24,000/m² — Monaco shown clipped for readability
      </text>
    </svg>
  );
};

export const StrChart = ({ rows }) => {
  const rowH = 52;
  const barX = 170;
  const barMax = 300;
  const revMax = 600;
  const h = rows.length * rowH + 30;
  return (
    <svg
      viewBox={`0 0 600 ${h}`}
      role="img"
      aria-label="Short-term rental occupancy and RevPAR by area, 2026"
      data-testid="report-str-chart"
      className="w-full h-auto"
    >
      {rows.map((r, i) => {
        const occ = num(r.occ);
        const rev = num(r.revpar);
        const y = i * rowH + 14;
        return (
          <g key={r.area}>
            <text x="0" y={y + 10} fill={INK} fontSize="11.5" style={fontA}>
              {r.area.length > 22 ? r.area.slice(0, 21) + "…" : r.area}
            </text>
            <rect x={barX} y={y} width={barMax} height={12} fill={TRACK} />
            <rect x={barX} y={y} width={(occ / 100) * barMax} height={12} fill={GOLD}>
              <animate attributeName="width" from="0" to={(occ / 100) * barMax} dur="0.7s" fill="freeze" />
            </rect>
            <text x={barX + (occ / 100) * barMax + 8} y={y + 10} fill={GOLD} fontSize="11" style={fontA}>
              {r.occ}
            </text>
            <rect x={barX} y={y + 16} width={barMax} height={8} fill={TRACK} />
            <rect x={barX} y={y + 16} width={(Math.min(rev, revMax) / revMax) * barMax} height={8} fill={GOLD_DIM}>
              <animate attributeName="width" from="0" to={(Math.min(rev, revMax) / revMax) * barMax} dur="0.7s" fill="freeze" />
            </rect>
            <text x={barX + (Math.min(rev, revMax) / revMax) * barMax + 8} y={y + 24} fill={MUTED} fontSize="10.5" style={fontA}>
              {r.revpar}
            </text>
          </g>
        );
      })}
      <g style={fontA} fontSize="10">
        <rect x="0" y={h - 14} width="10" height="10" fill={GOLD} />
        <text x="16" y={h - 5} fill={MUTED}>Occupancy (0–100%)</text>
        <rect x="160" y={h - 14} width="10" height="10" fill={GOLD_DIM} />
        <text x="176" y={h - 5} fill={MUTED}>RevPAR (scale €0–600/night)</text>
      </g>
    </svg>
  );
};

const DONUT_COLORS = [
  "hsl(43 60% 55%)", "hsl(205 55% 55%)", "hsl(0 55% 58%)", "hsl(160 35% 48%)",
  "hsl(28 60% 55%)", "hsl(268 30% 60%)", "hsl(190 35% 45%)", "hsl(45 12% 62%)",
];

export const BuyerDonut = ({ rows }) => {
  const r = 62;
  const c = 2 * Math.PI * r;
  let acc = 0;
  const total = rows.reduce((s, b) => s + num(b.share), 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-8" data-testid="report-buyer-chart">
      <svg viewBox="0 0 180 180" role="img" aria-label="Foreign buyer nationality mix, Alpes-Maritimes 2025" className="w-44 h-44 shrink-0">
        {rows.map((b, i) => {
          const share = num(b.share) / total;
          const dash = share * c;
          const offset = -acc * c;
          acc += share;
          return (
            <circle
              key={b.country}
              cx="90" cy="90" r={r} fill="none"
              stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth="26"
              strokeDasharray={`${dash - 2} ${c - dash + 2}`}
              strokeDashoffset={offset}
              transform="rotate(-90 90 90)"
            />
          );
        })}
        <text x="90" y="86" textAnchor="middle" fill={INK} fontSize="20" style={fontA}>38%</text>
        <text x="90" y="103" textAnchor="middle" fill={MUTED} fontSize="8.5" style={fontA}>OF ALL BUYERS</text>
        <text x="90" y="114" textAnchor="middle" fill={MUTED} fontSize="8.5" style={fontA}>ARE FOREIGN</text>
      </svg>
      <ul className="grid grid-cols-2 gap-x-8 gap-y-2 font-body text-sm">
        {rows.map((b, i) => (
          <li key={b.country} className="flex items-center gap-2.5">
            <span className="w-3 h-3 shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="text-foreground/80 text-xs">{b.country}</span>
            <span className="ml-auto font-headings text-sm text-foreground">{b.share}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const TaxRangeChart = ({ rows }) => {
  const ranges = rows
    .map((r) => {
      const m = String(r.value).match(/([\d.]+)%\s*[–-]\s*([\d.]+)%/);
      return m ? { label: r.label, min: parseFloat(m[1]), max: parseFloat(m[2]) } : null;
    })
    .filter(Boolean);
  const scale = 8;
  const rowH = 40;
  const barX = 240;
  const barMax = 300;
  const h = ranges.length * rowH + 26;
  return (
    <svg
      viewBox={`0 0 600 ${h}`}
      role="img"
      aria-label="Typical French property tax and fee ranges, 2026"
      data-testid="report-tax-chart"
      className="w-full h-auto"
    >
      {[0, 2, 4, 6, 8].map((tick) => (
        <g key={tick}>
          <line
            x1={barX + (tick / scale) * barMax} y1="6"
            x2={barX + (tick / scale) * barMax} y2={h - 20}
            stroke={TRACK} strokeWidth="1"
          />
          <SvgLabel x={barX + (tick / scale) * barMax} y={h - 6} textAnchor="middle" fill={MUTED} fontSize="10" text={`${tick}%`} />
        </g>
      ))}
      {ranges.map((r, i) => {
        const y = i * rowH + 16;
        const x1 = barX + (r.min / scale) * barMax;
        const x2 = barX + (r.max / scale) * barMax;
        return (
          <g key={r.label}>
            <SvgLabel x="0" y={y + 9} fill={INK} fontSize="11" text={r.label.length > 32 ? r.label.slice(0, 31) + "…" : r.label} />
            <rect x={x1} y={y} width={Math.max(x2 - x1, 4)} height={12} fill={GOLD} rx="1" />
            <circle cx={x1} cy={y + 6} r="4" fill={GOLD} />
            <circle cx={x2} cy={y + 6} r="4" fill={GOLD} />
            <SvgLabel x={x2 + 10} y={y + 10} fill={GOLD} fontSize="11" text={`${r.min}–${r.max}%`} />
          </g>
        );
      })}
    </svg>
  );
};
